import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminShell from "../../components/AdminShell";

/**
 * Mission Control — Admin Analytics Dashboard.
 * 11 sections. Populated by /api/admin/analytics/* endpoints where data exists;
 * uses a "Data pending" placeholder tile for metrics that require event tracking
 * to accrue (they will populate automatically as track() events flow in).
 */
export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [community, setCommunity] = useState(null);
  const [live, setLive] = useState(null);
  const [commerce, setCommerce] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [refs, setRefs] = useState(null);
  const [notifs, setNotifs] = useState(null);
  const [drops, setDrops] = useState(null);
  const [behaviour, setBehaviour] = useState(null);
  const [insights, setInsights] = useState(null);
  const [range, setRange] = useState(30);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, c, l, cm, f, r, n, d, b, ins] = await Promise.all([
          adminApi.get("/admin/analytics/summary"),
          adminApi.get(`/admin/analytics/community?days=${range}`),
          adminApi.get("/admin/analytics/live"),
          adminApi.get(`/admin/analytics/commerce?days=${range}`),
          adminApi.get(`/admin/analytics/funnel?days=${range}`),
          adminApi.get("/admin/analytics/referrals"),
          adminApi.get(`/admin/analytics/notifications?days=${range}`),
          adminApi.get("/admin/analytics/drops?limit=50"),
          adminApi.get(`/admin/analytics/behaviour?days=${range}`),
          adminApi.get(`/admin/analytics/insights?days=${range}`),
        ]);
        setSummary(s.data); setCommunity(c.data); setLive(l.data);
        setCommerce(cm.data); setFunnel(f.data); setRefs(r.data);
        setNotifs(n.data); setDrops(d.data); setBehaviour(b.data); setInsights(ins.data);
      } catch (_e) { /* ignore transient errors */ }
    };
    load();
    const iv = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(iv);
  }, [range, tick]);

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  return (
    <AdminShell>
      {/* ==================== HEADER ==================== */}
      <div className="flex flex-wrap items-end justify-between gap-3" data-testid="analytics-header">
        <div>
          <div className="brand-tag">// Mission Control</div>
          <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Analytics</h1>
          <div className="mt-2 font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#6E6E6E]">
            {dateStr} · {timeStr} IST · {summary?.current_live?.title ? `Drop #${summary.current_live.drop_no || "—"}` : "No drop scheduled"} · <LiveStatusPill live={summary?.current_live} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RangeSelect value={range} onChange={setRange} />
          <button onClick={() => setTick((t) => t + 1)} className="h-10 px-3 border border-[#E5E1D8] bg-[#FFFFFF] font-mono-ds text-[11px] uppercase tracking-[0.24em] hover:border-[#FF6A00] hover:text-[#FF6A00]" data-testid="analytics-refresh">
            Refresh ↻
          </button>
        </div>
      </div>

      {/* ==================== FOUNDER DAILY REPORT ==================== */}
      <FounderReport summary={summary} live={live} refs={refs} />

      {/* ==================== SECTION 1 — Executive Summary ==================== */}
      <Section title="Executive Summary" number="01">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <BigKpi label="Total Registered" value={summary?.totals?.total_users} testid="kpi-total-users" />
          <BigKpi label="Founding Members" value={`${summary?.totals?.founding_members_current ?? 0} / ${summary?.totals?.founding_cap ?? 250}`} accent testid="kpi-founding" />
          <BigKpi label="Waitlist" value={summary?.totals?.waitlist_only} testid="kpi-waitlist" />
          <BigKpi label="New Today" value={summary?.today?.new_members} trend={summary?.today?.growth_pct_vs_yesterday} testid="kpi-new-today" />
        </div>
      </Section>

      {/* ==================== SECTION 2 — Community ==================== */}
      <Section title="Community" number="02" subtitle="Highest priority · Are more people joining? Are they returning?">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Total Members" value={community?.total_members} />
          <Kpi label="Access Granted" value={community?.access_granted} accent />
          <Kpi label="DAU" value={community?.dau} />
          <Kpi label="WAU" value={community?.wau} />
          <Kpi label="MAU" value={community?.mau} />
          <Kpi label="With Referral" value={community?.with_referral} />
          <Kpi label="Referral Rate" value={fmtPct(community?.referral_rate_pct)} accent />
          <Kpi label="Growth (30d)" value={community?.series ? seriesSum(community.series, "new_members") : "—"} />
        </div>
        <Sparkline
          title="Daily Member Growth"
          data={community?.series || []}
          xKey="date"
          yKey="new_members"
          testid="chart-growth"
        />
      </Section>

      {/* ==================== SECTION 3 — Live Performance ==================== */}
      <Section title="Live Performance" number="03" subtitle={live?.title ? `Latest drop · ${live.title}` : "No lives yet"}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Viewers" value={live?.participants} accent />
          <Kpi label="Peak Concurrent" value={live?.peak_viewers} />
          <Kpi label="Chat Messages" value={live?.chat_messages} />
          <Kpi label="Bookings" value={live?.bookings} />
          <Kpi label="Join Events" value={live?.join_events} />
          <Kpi label="Leave Events" value={live?.leave_events} />
          <Kpi label="Avg Watch Time" value="—" pending />
          <Kpi label="Watch Completion" value="—" pending />
        </div>
        <Sparkline
          title="Viewer Count Over Time"
          data={live?.viewer_series || []}
          xKey="t"
          yKey="viewers"
          testid="chart-viewers"
        />
      </Section>

      {/* ==================== SECTION 4 — Commerce ==================== */}
      <Section title="Commerce" number="04">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Orders" value={commerce?.total_orders} />
          <Kpi label="Revenue" value={`₹${(commerce?.total_revenue || 0).toLocaleString("en-IN")}`} accent />
          <Kpi label="AOV" value={`₹${commerce?.aov || 0}`} />
          <Kpi label="Pending" value={commerce?.pending_orders} />
          <Kpi label="Failed" value={commerce?.failed_orders} />
          <Kpi label="Cart Abandonment" value="—" pending />
          <Kpi label="Checkout Started" value={funnel?.steps?.find((s) => s.event === "booking_created")?.count ?? 0} />
          <Kpi label="Payment Success" value={funnel?.steps?.find((s) => s.event === "payment_success")?.count ?? 0} />
        </div>
        <Sparkline
          title="Daily Revenue"
          data={commerce?.daily_revenue || []}
          xKey="date"
          yKey="revenue"
          testid="chart-revenue"
        />
      </Section>

      {/* ==================== SECTION 5 — User Funnel ==================== */}
      <Section title="User Funnel" number="05" subtitle={`Last ${range} days · biggest drop-off flagged in orange`}>
        <FunnelChart steps={funnel?.steps || []} />
      </Section>

      {/* ==================== SECTION 6 — Referrals ==================== */}
      <Section title="Referrals" number="06">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Invites Used" value={refs?.invites_used_total} />
          <Kpi label="Successful Referrals" value={refs?.total_referred} accent />
          <Kpi label="Invite Acceptance" value={fmtPct(refs?.acceptance_rate_pct)} />
          <Kpi label="Avg Invites / Member" value={refs?.leaderboard?.length ? (refs.invites_used_total / refs.leaderboard.length).toFixed(1) : "0"} />
        </div>
        <Leaderboard rows={refs?.leaderboard || []} />
      </Section>

      {/* ==================== SECTION 7 — Notifications ==================== */}
      <Section title="Notification Analytics" number="07" subtitle="Calendar send + click tracked via /analytics/track">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Opted-in Users" value={notifs?.opted_in} accent />
          <Kpi label="Opt-in Rate" value={fmtPct(notifs?.opted_in_pct)} />
          <Kpi label=".ics Channel" value={notifs?.channel_ics} />
          <Kpi label="Google Channel" value={notifs?.channel_google} />
          <Kpi label="Sends" value={notifs?.sends} />
          <Kpi label="Clicks" value={notifs?.clicks} />
          <Kpi label="Click Rate" value={fmtPct(notifs?.click_rate_pct)} accent />
          <Kpi label="Delivery Rate" value="—" pending />
        </div>
      </Section>

      {/* ==================== SECTION 8 — Community Insights ==================== */}
      <Section title="Community Insights" number="08" subtitle="Post-drop survey · NPS + attend-tomorrow intent">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="NPS Score" value={insights?.nps_score ?? "—"} accent />
          <Kpi label='"Attend Tomorrow?"' value={fmtPct(insights?.attend_tomorrow_pct)} />
          <Kpi label="Total Responses" value={insights?.total_responses} />
          <Kpi label="NPS Responses" value={insights?.nps_responses} />
          <Kpi label="Promoters (9–10)" value={insights?.promoters} accent />
          <Kpi label="Passives (7–8)" value={insights?.passives} />
          <Kpi label="Detractors (0–6)" value={insights?.detractors} />
          <Kpi label="Will Attend Tomorrow" value={`${insights?.attend_yes ?? 0} / ${insights?.attend_total ?? 0}`} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          <Kpi label="WhatsApp Members" value="—" pending />
          <Kpi label="Product Votes" value="—" pending />
          <Kpi label="Feedback" value="—" pending />
          <Kpi label="Bug Reports" value="—" pending />
        </div>
      </Section>

      {/* ==================== SECTION 9 — Drop History ==================== */}
      <Section title="Drop History" number="09">
        <DropHistory drops={drops?.drops || []} />
      </Section>

      {/* ==================== SECTION 10 — User Behaviour ==================== */}
      <Section title="User Behaviour" number="10" subtitle="Session dwell + repeat sessions from analytics_events">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Home Views (rng)" value={funnel?.steps?.find((s) => s.event === "home_view")?.count ?? 0} />
          <Kpi label="Landing Views" value={funnel?.steps?.find((s) => s.event === "landing_view")?.count ?? 0} />
          <Kpi label="Live Joins" value={funnel?.steps?.find((s) => s.event === "live_join")?.count ?? 0} />
          <Kpi label="Calendar Opt-ins" value={funnel?.steps?.find((s) => s.event === "notify_calendar_click")?.count ?? 0} />
          <Kpi label="Avg Home Dwell" value={fmtDuration(behaviour?.avg_home_dwell_ms)} accent />
          <Kpi label="Repeat Sessions" value={behaviour?.repeat_sessions} />
          <Kpi label="Total Sessions" value={behaviour?.total_sessions} />
          <Kpi label="Repeat Rate" value={fmtPct(behaviour?.repeat_rate_pct)} />
        </div>
      </Section>

      {/* ==================== SECTION 11 — Operational Health ==================== */}
      <Section title="Operational Health" number="11" subtitle="Infra + payment health">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Payment Success" value={commerce ? fmtPct(commerce.total_orders / Math.max(1, commerce.total_orders + commerce.failed_orders) * 100) : "—"} accent />
          <Kpi label="Failed Payments" value={commerce?.failed_orders} />
          <Kpi label="Refund Rate" value="—" pending />
          <Kpi label="Server Health" value="OK" accent />
          <Kpi label="API Response" value="—" pending />
          <Kpi label="Streaming Quality" value="—" pending />
          <Kpi label="Buffer Rate" value="—" pending />
          <Kpi label="Live On-Time" value="—" pending />
        </div>
      </Section>

      <div className="mt-10 mb-6 border-t border-[#E5E1D8] pt-6 font-mono-ds text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]" data-testid="analytics-footer">
        // Data auto-refreshes every 30s · Metrics marked ◇ populate as track() events accrue
      </div>
    </AdminShell>
  );
}

/* ============================================================
 * Tiny composable UI
 * ============================================================ */

function LiveStatusPill({ live }) {
  if (!live?.status) return <span className="text-[#6E6E6E]">No live</span>;
  const color = live.status === "live" ? "text-[#FF6A00]" : live.status === "final_window" ? "text-[#FF6A00]" : live.status === "ended" ? "text-[#6E6E6E]" : "text-[#264733]";
  return <span className={`${color} font-bold`}>{live.status.replace("_", " ").toUpperCase()}</span>;
}

function RangeSelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-10 px-3 border border-[#E5E1D8] bg-[#FFFFFF] font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#161616] focus:border-[#FF6A00] outline-none"
      data-testid="analytics-range"
    >
      <option value={1}>Today</option>
      <option value={7}>Last 7 Days</option>
      <option value={30}>Last 30 Days</option>
      <option value={90}>Last 90 Days</option>
    </select>
  );
}

function FounderReport({ summary, live, refs }) {
  const s = summary?.today || {};
  const h = summary?.health || {};
  const statusEmoji = h.status === "excellent" ? "🟢" : h.status === "good" ? "🟡" : "🔴";
  const statusLabel = h.status === "excellent" ? "Excellent" : h.status === "good" ? "Good" : "Needs Attention";
  return (
    <div className="mt-6 border-2 border-[#161616] bg-[#FFFFFF] shadow-[6px_6px_0_#264733] p-6 md:p-8" data-testid="founder-report">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="brand-tag">// Founder Daily Report</div>
          <div className="font-display text-3xl md:text-4xl uppercase tracking-tight mt-1">
            {summary?.current_live?.title ? `Drop #${summary.current_live.drop_no || "—"}` : "Today"}
          </div>
        </div>
        <div className="text-right">
          <div className="brand-tiny text-[#6E6E6E]">Drop Health</div>
          <div className="font-display text-5xl leading-none tabular-nums text-[#264733]" data-testid="health-score">
            {h.score ?? 0}<span className="text-2xl text-[#6E6E6E]">/100</span>
          </div>
          <div className="font-mono-ds text-[11px] uppercase tracking-[0.24em] mt-1" data-testid="health-status">
            {statusEmoji} {statusLabel}
          </div>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="New Members" value={s.new_members ?? 0} />
        <Stat label="Attendance" value={live?.participants ?? 0} />
        <Stat label="Attendance %" value={fmtPct(s.attendance_rate)} />
        <Stat label="Orders" value={s.orders ?? 0} />
        <Stat label="Revenue" value={`₹${(s.revenue || 0).toLocaleString("en-IN")}`} accent />
        <Stat label="Referrals" value={s.referral_signups ?? 0} />
      </div>
      <div className="mt-3 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">
        {refs?.total_referred ? `${refs.total_referred} lifetime referrals · ${refs.invites_used_total} invites used` : "// referrals still accruing"}
      </div>
    </div>
  );
}

function Section({ title, number, subtitle, children }) {
  return (
    <section className="mt-8" data-testid={`section-${number}`}>
      <div className="flex items-end justify-between gap-2 mb-3">
        <div>
          <div className="brand-tag">// {number}</div>
          <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight mt-1">{title}</h2>
          {subtitle && <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] mt-1">{subtitle}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

function BigKpi({ label, value, trend, accent, testid }) {
  return (
    <div className={`border-2 ${accent ? "border-[#264733] bg-[#B9E641]/25" : "border-[#161616] bg-[#FFFFFF]"} p-5 shadow-[3px_3px_0_#161616]`} data-testid={testid}>
      <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">{label}</div>
      <div className="font-display text-4xl md:text-5xl tabular-nums leading-none mt-2">{value ?? "—"}</div>
      {trend !== null && trend !== undefined && trend !== 0 && (
        <div className={`mt-2 font-mono-ds text-[10px] uppercase tracking-[0.24em] ${trend >= 0 ? "text-[#264733]" : "text-[#FF6A00]"}`}>
          {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}% vs yesterday
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, pending }) {
  return (
    <div className={`border ${accent ? "border-[#264733] bg-[#B9E641]/20" : pending ? "border-dashed border-[#E5E1D8] bg-[#F8F7F2]" : "border-[#E5E1D8] bg-[#FFFFFF]"} p-3.5`}>
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#6E6E6E] flex items-center gap-1">
        {pending && <span className="text-[#FF6A00]">◇</span>}
        <span>{label}</span>
      </div>
      <div className={`font-display text-2xl tabular-nums leading-none mt-1.5 ${pending ? "text-[#6E6E6E]" : "text-[#161616]"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-[#FF6A00]" : "border-[#E5E1D8]"} bg-[#F8F7F2] px-3 py-2`}>
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.24em] text-[#6E6E6E]">{label}</div>
      <div className={`font-display text-xl tabular-nums leading-none mt-1 ${accent ? "text-[#FF6A00]" : "text-[#161616]"}`}>{value}</div>
    </div>
  );
}

function Sparkline({ title, data, xKey, yKey, testid }) {
  const cleaned = data.filter((d) => d[yKey] !== undefined && d[yKey] !== null);
  if (cleaned.length === 0) {
    return (
      <div className="mt-3 border border-dashed border-[#E5E1D8] bg-[#F8F7F2] p-6 text-center font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#6E6E6E]" data-testid={testid}>
        // {title} · no data yet
      </div>
    );
  }
  const max = Math.max(...cleaned.map((d) => Number(d[yKey]) || 0), 1);
  const w = 100 / Math.max(1, cleaned.length - 1);
  const points = cleaned.map((d, i) => `${(i * w).toFixed(2)},${(100 - (Number(d[yKey]) || 0) / max * 100).toFixed(2)}`).join(" ");
  return (
    <div className="mt-3 border border-[#E5E1D8] bg-[#FFFFFF] p-4" data-testid={testid}>
      <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] mb-2">// {title}</div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-28 stroke-[#264733]" fill="none">
        <polyline points={points} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        <polyline points={`0,100 ${points} 100,100`} fill="#B9E641" fillOpacity="0.35" stroke="none" />
      </svg>
      <div className="flex justify-between font-mono-ds text-[9px] uppercase tracking-[0.2em] text-[#6E6E6E] mt-1">
        <span>{cleaned[0]?.[xKey]}</span>
        <span>Peak: {max}</span>
        <span>{cleaned[cleaned.length - 1]?.[xKey]}</span>
      </div>
    </div>
  );
}

function FunnelChart({ steps }) {
  const max = Math.max(...steps.map((s) => s.count || 0), 1);
  return (
    <div className="border border-[#E5E1D8] bg-[#FFFFFF]" data-testid="funnel-chart">
      {steps.map((s, i) => {
        const w = ((s.count || 0) / max) * 100;
        const drop = s.is_biggest_drop;
        return (
          <div key={i} className="border-b last:border-b-0 border-[#F0EDE4] px-4 py-2.5 flex items-center gap-3">
            <div className="w-40 shrink-0 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#161616]">{s.label}</div>
            <div className="flex-1 h-6 bg-[#F8F7F2] relative overflow-hidden">
              <div className={`h-full ${drop ? "bg-[#FF6A00]" : "bg-[#B9E641]"}`} style={{ width: `${Math.max(2, w)}%` }} />
            </div>
            <div className="w-16 text-right font-display text-lg tabular-nums">{s.count ?? 0}</div>
            <div className="w-16 text-right font-mono-ds text-[10px] tabular-nums text-[#6E6E6E]">
              {s.drop_pct !== undefined ? `-${s.drop_pct}%` : ""}
              {drop && <span className="ml-1 text-[#FF6A00] font-bold">▼</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Leaderboard({ rows }) {
  if (!rows.length) {
    return <div className="mt-3 border border-dashed border-[#E5E1D8] bg-[#F8F7F2] p-6 text-center font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#6E6E6E]">// No referrals yet</div>;
  }
  return (
    <div className="mt-3 border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto" data-testid="leaderboard">
      <table className="w-full text-sm font-mono-ds">
        <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
          <tr>
            <th className="text-left p-3">#</th><th className="text-left p-3">Member</th>
            <th className="text-left p-3">No.</th><th className="text-left p-3">Invites Used</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.user_id} className="border-b border-[#F0EDE4]">
              <td className="p-3 text-[#264733] font-bold">{i + 1}</td>
              <td className="p-3">
                <div className="text-[#161616]">{r.name || "—"}</div>
                <div className="text-[10px] text-[#6E6E6E]">{r.email}</div>
              </td>
              <td className="p-3 font-display text-lg text-[#264733]">#{String(r.member_no || 0).padStart(3, "0")}</td>
              <td className="p-3 text-[#FF6A00] font-bold">{r.invites_used || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DropHistory({ drops }) {
  const [sortKey, setSortKey] = useState("scheduled_at");
  const [dir, setDir] = useState(-1);
  const sorted = useMemo(() => {
    const rows = [...drops];
    rows.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * dir;
    });
    return rows;
  }, [drops, sortKey, dir]);
  const flip = (k) => { if (sortKey === k) setDir(-dir); else { setSortKey(k); setDir(-1); } };
  const H = ({ k, label }) => (
    <th className="text-left p-3 cursor-pointer hover:text-[#FF6A00]" onClick={() => flip(k)}>
      {label} {sortKey === k && (dir === -1 ? "↓" : "↑")}
    </th>
  );
  return (
    <div className="border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto" data-testid="drop-history">
      <table className="w-full text-sm font-mono-ds min-w-[900px]">
        <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
          <tr>
            <H k="scheduled_at" label="Date" />
            <H k="title" label="Drop" />
            <H k="status" label="Status" />
            <H k="viewers" label="Viewers" />
            <H k="peak_viewers" label="Peak" />
            <H k="orders" label="Orders" />
            <H k="revenue" label="Revenue" />
            <H k="conversion_pct" label="Conv %" />
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#6E6E6E]">// No drops yet</td></tr>}
          {sorted.map((d) => (
            <tr key={d.live_id} className="border-b border-[#F0EDE4] hover:bg-[#FFFDF7]">
              <td className="p-3 text-[10px] text-[#6E6E6E]">{d.scheduled_at ? new Date(d.scheduled_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}</td>
              <td className="p-3 text-[#161616] truncate max-w-[240px]">{d.title || "—"}</td>
              <td className="p-3"><span className="text-[10px] uppercase tracking-[0.24em]">{d.status}</span></td>
              <td className="p-3 tabular-nums">{d.viewers}</td>
              <td className="p-3 tabular-nums text-[#264733]">{d.peak_viewers}</td>
              <td className="p-3 tabular-nums">{d.orders}</td>
              <td className="p-3 tabular-nums">₹{(d.revenue || 0).toLocaleString("en-IN")}</td>
              <td className="p-3 tabular-nums text-[#FF6A00]">{d.conversion_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================
 * Helpers
 * ============================================================ */
function fmtPct(x) {
  if (x === null || x === undefined || Number.isNaN(x)) return "—";
  return `${Number(x).toFixed(1)}%`;
}
function fmtDuration(ms) {
  if (!ms || Number.isNaN(ms)) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}
function seriesSum(series, key) {
  return series.reduce((s, r) => s + (r[key] || 0), 0);
}
